import React from "react";
import PdfLayout from '../PdfLayout';
import PdfLayoutWidget from './PdfLayoutWidget';
import { Screen } from '../../services';
import {
    isThermalPdfLayout,
    pickPreviewPortraitUrl,
} from '../../services/pdfLayoutCanvasPreviewUtils';

/** Class representing a PDF layout image widget. */
class OwnerContactImageWidget extends PdfLayoutWidget {

    constructor(properties) {
        super({
            ...(OwnerContactImageWidget.getDefaultOptions()),
            ...properties,
            type: PdfLayout.IMAGE,
            subType: PdfLayout.OWNER_CONTACT_IMAGE
        });
    }

    /**
     * Get this widget default options
     */
    static getDefaultOptions() {
        return {
            position: { x: 2, y: 2, z: 0 },
            dimensions : { width: 4, height: 4 },
            transform: { rotation: 0 },
            options: {
                style: {
                    align: 'center',
                    objectFit: "contain",
                    borderStyle: 'solid',
                    borderWidth: 0,
                    borderColor: '#000000',
                    borderRadius: 0,
                    padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    }
                },
            }
        };
    }

    /**
     * Return the list of options this widget handles
     */
    getHandledOptions() {
        return super.getHandledOptions().concat([
            PdfLayout.OPTION_OBJECT_FIT,
            PdfLayout.OPTION_ALIGNMENT,
            PdfLayout.OPTION_PADDING,
        ]);
    }

    /**
     * Return this widget preview as JSX string
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {String} lang
     *                 - {Object} setting
     */
    getPreview(props) {
        const { setting, lang } = props;

        const image = setting?.findImage(this.options?.type, lang);
        const hasSettingImage = Boolean(image?.rfc2397_data);
        const widthPx = Screen.cmToPx(this.dimensions?.width);
        const heightPx = Screen.cmToPx(this.dimensions?.height);
        const borderWidth = parseFloat(this.options?.style?.borderWidth) || 0;
        const borderRadius = this.options?.style?.borderRadius || 0;
        // Match Engine ImageWidget: square image clip; radius only on stroked frame.
        const frameRadius = borderWidth > 0 ? borderRadius : 0;
        const objectFit = this.options.style?.objectFit;

        const imageStyle = {
            width: '100%',
            height: '100%',
        };
        if (objectFit === 'cover' || objectFit === 'contain')
            imageStyle.objectFit = objectFit;
        if (hasSettingImage && isThermalPdfLayout(props.layoutTarget))
            imageStyle.filter = 'grayscale(100%)';

        const previewPortraitSrc = pickPreviewPortraitUrl(
            props.layoutTarget,
            props.previewPortraitSeed
        );

        return (
            <div style={{
                position: 'relative',
                textAlign: this.options?.style?.align,
                width: widthPx,
                height: heightPx,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: this.options?.style?.backgroundColor || 'transparent',
                border: `${borderWidth}px ${this.options?.style?.borderStyle} ${this.options?.style?.borderColor}`,
                borderRadius: `${frameRadius}px`,
                paddingTop: `${Screen.cmToPx(this.options?.style?.padding?.top || 0)}px`,
                paddingRight: `${Screen.cmToPx(this.options?.style?.padding?.right || 0)}px`,
                paddingBottom: `${Screen.cmToPx(this.options?.style?.padding?.bottom || 0)}px`,
                paddingLeft: `${Screen.cmToPx(this.options?.style?.padding?.left || 0)}px`,
                boxSizing: 'border-box',
            }}>
                {hasSettingImage ? (
                    <img
                        src={image.rfc2397_data}
                        style={imageStyle}
                        alt="Owner contact preview"
                        draggable={false}
                        onDragStart={e => e.preventDefault()}
                    />
                ) : (
                    <img
                        src={previewPortraitSrc}
                        style={imageStyle}
                        alt="Owner contact preview"
                        draggable={false}
                        onDragStart={e => e.preventDefault()}
                    />
                )}
            </div>
        );
    }

    /**
     * Return the icon to use on the editor button
     */
    getIcon() {
        return 'user';
    }
}

export default OwnerContactImageWidget;
